import { useTheme } from "@emotion/react";
import PropTypes from "prop-types";
import { Box, Button, Card, CardContent, CardMedia, Divider, IconButton, Popover, Skeleton, Tooltip, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { Link as RouterLink } from "react-router-dom";
import { ShareOutlined, ThumbUpAlt, ThumbUpOutlined } from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import { FacebookIcon, FacebookShareButton, TwitterIcon, TwitterShareButton, WhatsappIcon, WhatsappShareButton } from "react-share";

import defaultImage from "../assets/sva-pic.jpg";
import calendarImage from "../assets/calendar.png";
import { setProfileKey } from "../reducers/profile";
import axios from "../utils/axios";

const relativeTime = require("dayjs/plugin/relativeTime");

dayjs.extend(relativeTime);

const useStyles = makeStyles(() => ({
  linkTo: {
    cursor: "pointer",
    "&:hover": {
      boxShadow: (props) => props?.theme?.shadows?.[6],
    },
  },
  link: {
    textDecoration: "none",
  },
  a: {
    textDecoration: "none",
    cursor: "default",
    pointerEvents: "auto",
  },
  calendarImage: {
    marginRight: 13,
  },
  likeIcon: {
    paddingRight: 5,
  },
  popOver: {
    padding: 6,
    gap: 6,
    display: "flex",
  },
  defaultImageStyle: {
    opacity: 0.5,
  },
  cardContent: {
    paddingTop: 0,
  },
}));
export default function ActivityCard({
  id,
  image_url: imageUrl,
  title,
  description,
  starts,
  activities = [],
  navigateTo,
  num_attending: numAttending,
  ends,
  hideDateLine,
  hideFooter,
  // image_public_url: imagePublicUrl,
  is_public: isPublic,
  isProgramme,
}) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const classes = useStyles({ theme });

  const { bookmarked_activity_ids: bookmarkedActivityIds, id: userId } = useSelector((state) => state.profile);

  const [anchorEl, setAnchorEl] = useState(null);
  const [openShare, setOpenShare] = useState(false);
  const [imageURL, setImageURL] = useState(imageUrl);

  const handleCloseShare = useCallback((event) => {
    event.preventDefault();
    setAnchorEl(null);
    setOpenShare(true);
  }, []);

  useEffect(() => {
    if (imageUrl) {
      setImageURL(imageUrl);
    } else if (imageUrl === null) {
      setImageURL(defaultImage);
    }
  }, [imageUrl]);

  const imageMemo = useMemo(
    () =>
      imageURL ? (
        <CardMedia
          onError={() => setImageURL(defaultImage)}
          component="img"
          height="240"
          image={imageURL}
          alt="Activity"
          className={imageURL === defaultImage ? classes.defaultImageStyle : ""}
        />
      ) : (
        <Skeleton height={240} variant="rectangular" />
      ),
    [classes.defaultImageStyle, imageURL]
  );

  const handleInterested = useCallback(
    async (event) => {
      event.preventDefault();
      await axios.get(`bookmark/toggle/${id}`);
      if (!bookmarkedActivityIds.includes(id)) {
        dispatch(setProfileKey("bookmarked_activity_ids", [...bookmarkedActivityIds, id]));
      } else {
        dispatch(
          setProfileKey(
            "bookmarked_activity_ids",
            bookmarkedActivityIds.filter((number) => number !== id)
          )
        );
      }
    },
    [id, bookmarkedActivityIds, dispatch, isProgramme]
  );

  const handleSocial = useCallback(async (event) => {
    event.preventDefault();
    setAnchorEl(event.currentTarget);
    setOpenShare(true);
  }, []);

  const dates = useMemo(() => {
    let first;
    let last;
    let start = starts;
    let firstInFuture;
    let firstInFutureId;
    if (!start && activities?.length) {
      activities.forEach((activity) => {
        if (!first || dayjs(activity.starts).isBefore(dayjs(first))) {
          first = activity.starts;
        }
        if (!last || dayjs(activity.starts).isAfter(dayjs(last))) {
          last = activity.starts;
        }
        if (dayjs(activity.starts).isAfter() && (!firstInFuture || dayjs(activity.starts).isBefore(firstInFuture))) {
          firstInFuture = activity.starts;
          firstInFutureId = activity.id;
        }
      });
    }
    if (firstInFuture) {
      start = firstInFuture;
    }
    return { first, last, start, firstInFutureId };
  }, [activities, starts]);

  const dateLine = useMemo(() => {
    if (!title) {
      return "";
    }
    const { start, first, last } = dates;
    if (!start) {
      // All the activities are in the past
      if (last && first) {
        return `${dayjs(first).format("DD/MM/YYYY")} - ${dayjs(last).format("DD/MM/YYYY")}`;
      }
      return " ";
    }
    if (dayjs(start).isBefore()) {
      return dayjs(start).format("DD MMM 'YY");
    }
    if (dayjs(start).isBefore(dayjs().add(7, "d"))) {
      return `This ${dayjs(start).format("dddd")} at ${dayjs(start).format("HH:mm")}`;
    }
    return dayjs(start).format("DD MMM 'YY [at] HH:mm");
  }, [dates, title]);

  const dateLineMemo = useMemo(() => {
    const shareProps = {
      url: `${window.location.origin}/activities/${id}`,
      quote: "Come join me at this event!",
      hashtag: "#SVA #HelpingOut #SVAVolunteering",
      hashtags: ["SVAVolunteering", "SVA", "HelpingOut"],
      title,
    };

    const tooltipTitle = !isPublic ? "Only public activities can be shared" : "";
    return hideDateLine ? null : (
      <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center" my={0.5}>
        <Box display="flex" flexDirection="row">
          {dateLine && <img src={calendarImage} height={19} alt="Calendar" className={classes.calendarImage} />}
          <Typography
            sx={{
              flex: 1,
              fontSize: 14,
              fontWeight: 700,
              lineHeight: "16px",
              alignItems: "center",
            }}
            gutterBottom
          >
            {dateLine || <Skeleton width="50%" />}
          </Typography>
        </Box>
        {!dateLine ? (
          <Skeleton width={20} />
        ) : (
          <>
            <Tooltip title={tooltipTitle}>
              <span>
                <IconButton onClick={handleSocial} disabled={!isPublic}>
                  <ShareOutlined />
                </IconButton>
              </span>
            </Tooltip>
            {anchorEl && (
              <Popover
                id={id}
                open={openShare}
                anchorEl={anchorEl}
                onClose={handleCloseShare}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "center",
                }}
                classes={{ paper: classes.popOver }}
              >
                <FacebookShareButton {...shareProps}>
                  <FacebookIcon size={30} round />
                </FacebookShareButton>
                <TwitterShareButton {...shareProps}>
                  <TwitterIcon size={30} round />
                </TwitterShareButton>
                <WhatsappShareButton {...shareProps}>
                  <WhatsappIcon size={30} round />
                </WhatsappShareButton>
              </Popover>
            )}
          </>
        )}
      </Box>
    );
  }, [
    anchorEl,
    classes.calendarImage,
    classes.popOver,
    dateLine,
    handleCloseShare,
    handleSocial,
    hideDateLine,
    id,
    isPublic,
    openShare,
    title,
  ]);

  const titleMemo = useMemo(
    () => (
      <Typography
        sx={{
          display: "-webkit-box",
          overflow: "hidden",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: 1,
          fontSize: 20,
          fontWeight: 700,
          lineHeight: "23px",
        }}
        variant="h6"
        color="text.secondary"
        gutterBottom
      >
        {title || <Skeleton width="100%" />}
      </Typography>
    ),
    [title]
  );

  const textMemo = useMemo(
    () => (
      <Typography
        sx={{
          display: "-webkit-box",
          overflow: "hidden",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: 5,
          fontSize: 14,
          fontWeight: 400,
          lineHeight: "22px",
          height: [undefined, undefined, "110px"],
        }}
        variant="body2"
        color="text.secondary"
        gutterBottom
      >
        {description || (
          <>
            <Skeleton width="100%" />
            <Skeleton width="100%" />
            <Skeleton width="40%" />
          </>
        )}
      </Typography>
    ),
    [description]
  );

  const likeId = useMemo(() => {
    if (!isProgramme) {
      return id;
    }
    return dates?.firstInFutureId || undefined;
  }, [id, isProgramme, dates]);

  const likeMemo = useMemo(
    () => (
      <Box display="flex" flexDirection="row" alignItems="center" visibility={!id ? "hidden" : undefined}>
        <Box className={classes.likeIcon} display="flex" flexDirection="row" alignItems="center">
          <Button
            onClick={handleInterested}
            disabled={dayjs(ends).isBefore() || !userId || !likeId}
            sx={{ color: !bookmarkedActivityIds.includes(likeId) ? "#54585A" : "green.main" }}
            startIcon={
              bookmarkedActivityIds.includes(likeId) ? <ThumbUpAlt color="green" fontSize="small" /> : <ThumbUpOutlined fontSize="small" />
            }
          >
            {!userId ? "Sign in to s" : ""}
            {userId && !bookmarkedActivityIds.includes(likeId) ? "S" : ""}
            {bookmarkedActivityIds.includes(likeId) ? "You are interested" : "ave for later"}
          </Button>
        </Box>
        {!!numAttending && (
          <Typography sx={{ borderLeft: "0.5px solid rgba(32, 27, 81, 0.3)", paddingLeft: 1 }}>
            {numAttending && `${numAttending} ${numAttending === 1 ? "is" : "are"} going`}
          </Typography>
        )}
      </Box>
    ),
    [numAttending, classes.likeIcon, handleInterested, ends, bookmarkedActivityIds, likeId, userId]
  );

  return (
    <RouterLink to={navigateTo || "#"} className={classes.a}>
      <Card className={navigateTo ? classes.linkTo : classes.link}>
        {imageMemo}
        <CardContent className={classes.cardContent}>
          {dateLineMemo}
          <Divider />
          <Box my={2}>
            {titleMemo}
            {textMemo}
          </Box>
          {!title ? <Skeleton width="50%" /> : null}
          {!hideFooter && likeMemo}
        </CardContent>
      </Card>
    </RouterLink>
  );
}

ActivityCard.defaultProps = {
  id: undefined,
  image_url: undefined,
  title: undefined,
  description: undefined,
  starts: undefined,
  activities: [],
  navigateTo: undefined,
  num_attending: undefined,
  ends: undefined,
  hideDateLine: false,
  hideFooter: false,
  isProgramme: false,
  image_public_url: null,
  is_public: 0,
};

ActivityCard.propTypes = {
  id: PropTypes.number,
  image_url: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  starts: PropTypes.string,
  activities: PropTypes.arrayOf(
    PropTypes.shape({
      starts: PropTypes.string,
    })
  ),
  navigateTo: PropTypes.string,
  num_attending: PropTypes.number,
  ends: PropTypes.string,
  hideDateLine: PropTypes.bool,
  hideFooter: PropTypes.bool,
  image_public_url: PropTypes.string,
  is_public: PropTypes.number,
  isProgramme: PropTypes.bool,
};
